import React, { useState, useEffect, useRef } from 'react';
import intelSymbol from './photos/intel.png';
import scsSymbol from './photos/focus_partners.jpeg';
import dfdsSymbol from './photos/dfds.png';
import dogsPhoto from './photos/dog.JPG';
import sportsPhoto from './photos/sport.jpg';
import guitarPhoto from './photos/instrument.jpg';
import buSymbol from './photos/buLogo.png';
import travelPhoto from './photos/sunrise.jpeg';
import photographyPic from './photos/photography.jpg';
import concertSymbol from './photos/concertPhoto.JPG';
import creavitSymbol from './photos/creavit.png'
import { Mail, Github, Linkedin, Play, Menu, X, Download } from "lucide-react";
import photograph1 from './photos/photograph1.JPG';
import photograph2 from './photos/photograph2.JPG';
import photograph3 from './photos/photograph3.JPG';
import photograph4 from './photos/photograph4.JPG';
import photograph5 from './photos/photograph5.JPG';
import photograph6 from './photos/photograph6.JPG';
import photograph7 from './photos/photograph7.JPG';
import photograph8 from './photos/photograph8.JPG';
import photograph9 from './photos/photograph9.JPG';
import photograph10 from './photos/photograph10.JPG';
import photograph11 from './photos/photograph11.JPG';
import photograph12 from './photos/photograph12.jpg';
import photograph14 from './photos/photograph14.jpeg';
import photograph15 from './photos/photograph15.JPG';
import photograph13 from './photos/photograph13.JPG';
import photograph16 from './photos/fourth_of_july.JPG'
import photograph17 from './photos/san_fran_1.JPG'
import photograph18 from './photos/downtown_sf.JPG'
import photograph19 from './photos/puerto_rico.JPG'
import photograph20 from './photos/acadia.JPG'
import { FaYoutube } from "react-icons/fa";

const SECTION_NAMES = ['Home', 'About', 'Work Experience', 'Education', 'Projects', 'Personal', 'More'];

const PersonalWebsite = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('Home');
  const [revealedSections, setRevealedSections] = useState(() => new Set(['Home']));

  //lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Hero video background preferences
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Projects filter
  const [projectFilter, setProjectFilter] = useState('All');

  const sectionRefs = useRef({});
  const heroVideoRef = useRef(null);
  const ratiosRef = useRef({});

  // Cinematic hero reveal - plays once on first load
  useEffect(() => {
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setIsLoaded(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  useEffect(() => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const updateMotion = () => setPrefersReducedMotion(motionQuery.matches);
    updateMotion();

    motionQuery.addEventListener('change', updateMotion);
    return () => {
      motionQuery.removeEventListener('change', updateMotion);
    };
  }, []);

  // Show the looping video on every screen size now; only skip it when the
  // user's OS explicitly asks for reduced motion (accessibility), falling
  // back to the poster image in that case.
  const showHeroVideo = !prefersReducedMotion;

  // Track which section is on screen (for nav highlighting/transparency) and
  // mark sections as "revealed" the first time they scroll into view, so each
  // one fades/rises in as you scroll down the page instead of all at once.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const name = entry.target.dataset.section;
          if (!name) return;
          ratiosRef.current[name] = entry.isIntersecting ? entry.intersectionRatio : 0;
          if (entry.isIntersecting) {
            setRevealedSections((prev) => (prev.has(name) ? prev : new Set(prev).add(name)));
          }
        });

        const [bestName, bestRatio] = Object.entries(ratiosRef.current).reduce(
          (best, cur) => (cur[1] > best[1] ? cur : best),
          ['Home', 0]
        );
        if (bestRatio > 0) setActiveSection(bestName);
      },
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Gentle parallax on the hero video as you scroll through it
  useEffect(() => {
    if (!showHeroVideo) return undefined;
    let raf = null;
    const handleScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        if (heroVideoRef.current) {
          const offset = Math.min(window.scrollY * 0.3, 160);
          heroVideoRef.current.style.transform = `translateY(${offset}px) scale(1.08)`;
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [showHeroVideo]);

  const goTo = (name) => {
    setMobileNavOpen(false);
    sectionRefs.current[name]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setSectionRef = (name) => (el) => {
    sectionRefs.current[name] = el;
  };

  const sectionRevealClass = (name) =>
    `transition-all duration-700 ease-out ${
      revealedSections.has(name) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
    }`;

  // Re-encodes a photo client-side (via canvas) so visitors can grab it in
  // whichever format they want, rather than just whatever the source file is.
  const downloadPhoto = (photo, format) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (format === 'jpeg') {
        // JPEG has no transparency channel - flatten onto white first.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, 0, 0);

      const mime = format === 'png' ? 'image/png' : format === 'webp' ? 'image/webp' : 'image/jpeg';
      const extension = format === 'jpeg' ? 'jpg' : format;
      const slug = (photo.description || 'photo')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      canvas.toBlob((blob) => {
        if (!blob) return;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${slug || 'photo'}-${photo.id}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }, mime, format === 'jpeg' ? 0.92 : undefined);
    };
    img.src = photo.url;
  };

  const skills = [
    {
      key: 'Software',
      description: 'Building tools that work.',
      tags: ['Python', 'Java', 'SQL', 'JavaScript (React)', 'Go', 'GitHub', 'Docker'],
    },
    {
      key: 'Data',
      description: 'Using data to uncover insights.',
      tags: ['Python', 'Pandas', 'NumPy', 'scikit-learn', 'SQL', 'Power BI'],
    },
    {
      key: 'Finance',
      description: 'Analyzing financial markets.',
      tags: ['Financial Modeling', 'Risk Analysis', 'Portfolio Optimization'],
    },
  ];

  const experience = [
    {
      company: 'SCS Financial',
      role: 'Technology Associate',
      logo: scsSymbol,
      description: 'Started in September 2025.',
    },
    {
      company: 'Intel',
      role: 'Automation Intern',
      logo: intelSymbol,
      description:
        'Built and enhanced an internal AI chatbot at Intel, streamlining support for 3,000+ employees and handling 7,500+ daily requests. Improved context awareness with Intel-specific acronym handling and authored onboarding docs for 300+ new hires. Presented tool across 30+ departments, driving a 30% boost in efficiency.',
    },
    {
      company: 'DFDS',
      role: 'Integration Development Intern',
      logo: dfdsSymbol,
      description:
        'Automated XML error handling with 500+ lines of Java, helping 15 team members troubleshoot shipping issues faster. Managed real-time route data for 11 vendors in MySQL Server and streamlined GitHub workflows to support a smooth merger with a buyer firm.',
    },
    {
      company: 'Creavit',
      role: 'Software Development Intern',
      logo: creavitSymbol,
      description:
        'Developed Python-based client solutions for 60+ users by automating keyword search and data routing via Jira, streamlining workflow distribution. Implemented secure data transfer protocols and access controls, proactively preventing 7 potential security incidents.',
    },
  ];

  const projectsData = [
    {
      id: 'portfolio',
      title: 'Portfolio',
      category: 'Software',
      description: 'My responsive portfolio site, built with React and Tailwind CSS. Deployed and running on Vercel.',
      tags: ['React', 'Tailwind CSS', 'Vercel'],
      url: 'https://github.com/tan-aydemir/portfolio',
    },
    {
      id: 'dormquery',
      title: 'DormQuery Agent',
      category: 'Software',
      description: 'A smart building analytics tool that combines graph modeling, time-series sensor simulation, and LLM-powered natural language querying to deliver intuitive insights into room conditions, AC unit coverage, and occupancy trends.',
      tags: ['Python', 'Graph Modeling', 'LLM', 'Time-Series'],
      url: 'https://github.com/tan-aydemir/dormquery-agent/tree/main',
    },
    {
      id: 'lsa',
      title: 'LSA Search Engine',
      category: 'Software',
      description: 'This project implements a basic search engine using Latent Semantic Analysis. It allows users to query a predefined dataset and retrieve the most relevant documents based on cosine similarity. The web application includes a responsive user interface and visualization for query results.',
      tags: ['Python', 'LSA', 'Cosine Similarity'],
      url: 'https://github.com/tan-aydemir/Latent-Semantic-Analysis-Search-Engine-WebPage',
    },
    {
      id: 'macrorate',
      title: 'MacroRateTrading',
      category: 'Finance',
      description: 'A rule-based trading framework that utilizes long-term interest rate movements to generate buy/sell signals, apply transaction costs, and evaluate strategy, market, and abnormal returns through comparative performance analysis.',
      tags: ['Python', 'Backtesting', 'Quant'],
      url: 'https://github.com/tan-aydemir/quantitative-finance/tree/main/backtesting-interest-driven-trading-strategy',
    },
    {
      id: 'bsm',
      title: 'Black-Scholes-Merton Option Pricing Model',
      category: 'Finance',
      description: 'A comprehensive Python implementation of the Black-Scholes-Merton model for pricing European options, computing Greeks, generating sensitivity tables, and estimating implied volatility using numerical methods.',
      tags: ['Python', 'Options Pricing', 'Greeks'],
      url: 'https://github.com/tan-aydemir/quantitative-finance/tree/main/black-scholes-merton-option-pricing',
    },
    {
      id: 'resume',
      title: 'Resume Evaluation Agent',
      category: 'Data',
      description: 'A machine learning system that analyzes resumes to assess candidate profiles and predict potential future job titles and industries, enabling users to pre-screen their CVs and explore career trajectories.',
      tags: ['Python', 'Machine Learning', 'NLP'],
      url: 'https://github.com/tan-aydemir/CS506-Final-Project_Resume_Evaluation_Agent',
    },
    {
      id: 'regression',
      title: 'Regression Inference Simulator',
      category: 'Data',
      description: 'An interactive web application that extends linear regression analysis with hypothesis testing and confidence intervals, enabling users to simulate datasets, test parameter significance, and visualize the impact of randomness on regression estimates.',
      tags: ['Python', 'Statistics', 'Regression'],
      url: 'https://github.com/tan-aydemir/Hypothesis-Testing-and-Confidence-Intervals-in-Linear-Regression',
    },
  ];

  const filteredProjects = projectFilter === 'All'
    ? projectsData
    : projectsData.filter((p) => p.category === projectFilter);

  const outsideOfCode = [
    {
      key: 'tennis',
      title: 'Tennis',
      photo: sportsPhoto,
      text: "I played tennis for over 16 years, and competed in both national and international tournaments. Being part of the Olympic Prep Team gave me valuable experience in high-level training and contributed to my sense of discipline and focus.",
    },
    {
      key: 'dogs',
      title: 'Dogs',
      photo: dogsPhoto,
      text: "I have four dogs — a Shih Tzu, a Doberman, an Anatolian Shepherd, and a Cocker Spaniel. Whenever I'm home, I love taking them out for long walks.",
    },
    {
      key: 'guitar',
      title: 'Guitar',
      photo: guitarPhoto,
      text: 'I have played the guitar for over 7 years.',
      link: { href: 'https://www.youtube.com/@Tan-Aydemir', label: 'Watch on YouTube' },
    },
    {
      key: 'travel',
      title: 'Traveling',
      photo: travelPhoto,
      text: 'I love exploring new places and cultures. My favorite destinations are 🇳🇴, 🇫🇮, 🇵🇷, and 🇬🇧.',
    },
    {
      key: 'music',
      title: 'Music',
      photo: concertSymbol,
      text: 'I love 70s and 80s music. The Beatles, Pink Floyd, Queen, and Fleetwood Mac are some of my all-time favorite bands.',
    },
    {
      key: 'photography',
      title: 'Photography',
      photo: photographyPic,
      text: "I enjoy photographing nature, and I've included some of my favorite shots below.",
    },
  ];

  const photographyData = [
    { id: 1,  url: photograph1,  title: '',  description: 'Boston, Massachussetts' },
    { id: 2,  url: photograph2,  title: '',  description: 'Phoenix, Arizona' },
    { id: 3,  url: photograph3,  title: '',  description: 'Boston, Massachussetts' },
    { id: 4,  url: photograph4,  title: '',  description: 'İstanbul, Türkiye' },
    { id: 5,  url: photograph5,  title: '',  description: 'Blue Ridge Mountains, Virginia' },
    { id: 6,  url: photograph6,  title: '',  description: 'Datça, Türkiye' },
    { id: 7,  url: photograph7,  title: '',  description: 'Dalyan, Türkiye' },
    { id: 8,  url: photograph8,  title: '',  description: 'Datça, Türkiye' },
    { id: 9,  url: photograph9,  title: '',  description: 'Sedona, Arizona' },
    { id: 10, url: photograph10, title: '', description: 'Boston, Massachussetts' },
    { id: 11, url: photograph11, title: '', description: 'London, UK' },
    { id: 12, url: photograph12, title: '', description: 'Datça, Türkiye' },
    { id: 13, url: photograph13, title: '', description: 'Datça, Türkiye' },
    { id: 14, url: photograph14, title: '', description: 'Bergen, Norway' },
    { id: 15, url: photograph15, title: '', description: 'San Juan, Puerto Rico' },
    { id: 16, url: photograph16, title: '', description: 'Boston, Massachussetts' },
    { id: 17, url: photograph17, title: '', description: 'San Francisco, California' },
    { id: 18, url: photograph18, title: '', description: 'San Francisco, California' },
    { id: 19, url: photograph19, title: '', description: 'San Juan, Puerto Rico' },
    { id: 20, url: photograph20, title: '', description: 'Acadia National Park, Maine' },
  ];

  // A small, hand-set editorial mark used to open each section - a serif numeral and a
  // tracked-out label, instead of a repeated icon-in-a-circle before every heading.
  const SectionMark = ({ index, label }) => (
    <div className="flex items-baseline gap-3 mb-4">
      <span className="font-serif italic text-2xl text-slate-400">{index}</span>
      <span className="text-[11px] uppercase tracking-[0.4em] text-slate-400">{label}</span>
    </div>
  );

  const isHome = activeSection === 'Home';

  return (
    <div className="min-h-screen bg-[#111c2e]">
      {/* Navigation Header - transparent glass over the hero video, solidifies once you scroll past it */}
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          isHome
            ? 'bg-gradient-to-b from-black/50 to-transparent'
            : 'bg-[#111c2e]/90 backdrop-blur-md border-b border-white/10'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between md:justify-center items-center py-5">
            {/* Desktop nav */}
            <div className="hidden md:flex flex-wrap justify-center gap-7">
              {SECTION_NAMES.map((item) => (
                <button
                  key={item}
                  onClick={() => goTo(item)}
                  className={`pb-1 font-cinematic text-xs uppercase tracking-[0.2em] border-b transition-colors duration-300 [text-shadow:0_1px_4px_rgba(0,0,0,0.5)] ${
                    activeSection === item
                      ? 'text-white border-[#60a5fa]'
                      : 'text-gray-300 border-transparent hover:text-white hover:border-white/40'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            {/* Mobile: brand + hamburger */}
            <span className="md:hidden font-serif italic text-white tracking-wide [text-shadow:0_1px_4px_rgba(0,0,0,0.5)]">
              Tan Aydemir
            </span>
            <button
              className="md:hidden text-white p-2 -mr-2"
              onClick={() => setMobileNavOpen((v) => !v)}
              aria-label="Toggle navigation menu"
            >
              {mobileNavOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown panel */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            mobileNavOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          } ${isHome ? 'bg-black/60 backdrop-blur-md' : 'bg-[#111c2e]/95 backdrop-blur-md'} border-b border-white/10`}
        >
          <div className="px-4 py-3 flex flex-col gap-1">
            {SECTION_NAMES.map((item) => (
              <button
                key={item}
                onClick={() => goTo(item)}
                className={`w-full text-left px-2 py-3 font-cinematic text-xs uppercase tracking-[0.2em] border-b border-white/5 transition-colors duration-200 ${
                  activeSection === item ? 'text-[#60a5fa]' : 'text-gray-300 hover:text-white'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* ================= HOME / HERO ================= */}
      {/* The section is taller than one screen: the first 100vh is 100% video with nothing
          else visible, and the video-to-background transition lives entirely in the extra
          height below that, so it only appears once the user actually scrolls down. */}
      <section
        ref={setSectionRef('Home')}
        data-section="Home"
        className="relative h-[112vh] w-full overflow-hidden"
      >
        {showHeroVideo ? (
          <video
            ref={heroVideoRef}
            className="absolute inset-x-0 top-0 h-screen w-full object-cover z-0 will-change-transform"
            style={{ transform: 'translateY(0px) scale(1.08)' }}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={`${process.env.PUBLIC_URL}/images/turtle-poster.jpg`}
          >
            <source src={`${process.env.PUBLIC_URL}/videos/turtle-hero.mp4`} type="video/mp4" />
          </video>
        ) : (
          <img
            src={`${process.env.PUBLIC_URL}/images/turtle-poster.jpg`}
            alt="Sea turtle swimming"
            className="absolute inset-x-0 top-0 h-screen w-full object-cover z-0"
          />
        )}

        {/* A single, easy fade straight to the page background color - no intermediate hues,
            just a long, gently-eased ramp so the video simply dissolves into the page rather
            than cutting or passing through a visibly different color band. The whole thing
            lives below the first 100vh, so it's offscreen until scrolled into view. */}
        <div
          className="absolute inset-x-0 bottom-0 h-[26vh] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(to bottom, rgba(17,28,46,0) 0%, rgba(17,28,46,0.08) 30%, rgba(17,28,46,0.22) 50%, rgba(17,28,46,0.48) 68%, rgba(17,28,46,0.8) 84%, #111c2e 100%)',
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-16 sm:h-20 bg-[#111c2e] pointer-events-none" />

        {/* Hero content - name emerges cinematically first, then a thin divider draws in,
            then the buttons rise in as glassy pill CTAs, like a streaming title card. Pinned
            to the same first 100vh as the video so it's always framed correctly on load. */}
        <div className="absolute inset-x-0 top-0 h-screen z-10 w-full flex flex-col items-center justify-center px-6">
          <div className="max-w-6xl mx-auto w-full flex flex-col items-center">

            <h1
              className={`font-cinematic font-black text-4xl sm:text-5xl md:text-6xl lg:text-8xl text-white text-center uppercase [text-shadow:0_0_40px_rgba(0,0,0,0.65),0_2px_10px_rgba(0,0,0,0.5)] transition-all ease-out ${
                isLoaded
                  ? 'opacity-100 tracking-[0.08em] sm:tracking-[0.12em] lg:tracking-[0.15em] scale-100 blur-none duration-[1800ms] delay-500'
                  : 'opacity-0 tracking-[0.3em] scale-110 blur-md duration-0'
              }`}
            >
              Tan Aydemir
            </h1>

            <div
              className={`h-px bg-white/60 mt-6 sm:mt-8 transition-all duration-1000 ease-out ${
                isLoaded ? 'w-28 opacity-100 delay-[1700ms]' : 'w-0 opacity-0'
              }`}
            />

            <div
              className={`flex flex-col sm:flex-row gap-4 sm:gap-5 items-center mt-8 sm:mt-10 w-full sm:w-auto transition-all duration-1000 delay-[2000ms] ease-out ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <button
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-9 py-4 bg-white text-gray-900 font-cinematic font-bold uppercase tracking-[0.15em] text-sm rounded-full shadow-2xl hover:shadow-[0_0_45px_rgba(255,255,255,0.55)] transform hover:scale-105 transition-all duration-300"
                onClick={() => goTo('Projects')}
              >
                <Play className="w-4 h-4 fill-gray-900 group-hover:fill-slate-600 transition-colors duration-300" />
                Explore My Work
              </button>

              <button
                className="w-full sm:w-auto px-9 py-4 bg-white/10 backdrop-blur-md border border-white/40 text-white font-cinematic font-semibold uppercase tracking-[0.15em] text-sm rounded-full hover:bg-white/20 hover:border-white/80 transform hover:scale-105 transition-all duration-300"
                onClick={() => (window.location.href = 'mailto:taydemir@bu.edu?subject=')}
              >
                Contact Me
              </button>
            </div>
          </div>

          {/* Scroll cue */}
          <div
            className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70 transition-opacity duration-1000 delay-[2400ms] ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
            <div className="w-px h-8 bg-gradient-to-b from-white/70 to-transparent animate-pulse" />
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section
        ref={setSectionRef('About')}
        data-section="About"
        className={`w-full px-6 sm:px-8 md:px-12 pt-4 sm:pt-6 pb-10 sm:pb-14 scroll-mt-20 ${sectionRevealClass('About')}`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <span className="flex-1 h-px bg-white/20" />
            <span className="w-1.5 h-1.5 rotate-45 border border-white/40 flex-shrink-0" />
            <span className="flex-1 h-px bg-white/20" />
          </div>
          <SectionMark index="01" label="About" />

          <p className="font-serif text-2xl sm:text-3xl text-slate-100 leading-relaxed border-l-2 border-white/25 pl-6 sm:pl-8">
            I studied computer science and business at Boston University, and I now work as a software
            engineer in finance.{' '}
            <span className="text-slate-400">
              I'm drawn to the overlap between software, data, and financial systems — I like building
              tools that work and that people actually use daily.
            </span>
          </p>

          <div className="mt-14 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-10">
            {skills.map((skill) => (
              <div key={skill.key} className="border-t border-white/10 pt-5">
                <h3 className="text-white font-semibold text-lg mb-1">{skill.key}</h3>
                <p className="text-slate-400 text-sm mb-3">{skill.description}</p>
                <p className="text-slate-500 text-xs leading-relaxed tracking-wide">
                  {skill.tags.join(' · ')}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= WORK EXPERIENCE ================= */}
      <section
        ref={setSectionRef('Work Experience')}
        data-section="Work Experience"
        className={`w-full px-6 sm:px-8 md:px-12 py-10 sm:py-14 scroll-mt-20 ${sectionRevealClass('Work Experience')}`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <span className="flex-1 h-px bg-white/20" />
            <span className="w-1.5 h-1.5 rotate-45 border border-white/40 flex-shrink-0" />
            <span className="flex-1 h-px bg-white/20" />
          </div>
          <SectionMark index="02" label="Experience" />
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-14 sm:mb-16">Where I've worked</h2>

          <div className="relative border-l border-white/15 ml-2">
            {experience.map((job) => (
              <div key={job.company} className="relative pl-8 sm:pl-10 pb-12 last:pb-0">
                <span className="absolute -left-[5px] top-1.5 w-[9px] h-[9px] rounded-full bg-[#60a5fa]" />

                <div className="flex items-center gap-3 mb-1.5">
                  <div className="w-7 h-7 rounded-sm bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img src={job.logo} alt={`${job.company} logo`} className="w-full h-full object-contain p-0.5" />
                  </div>
                  <h3 className="text-white font-semibold text-lg">{job.role}</h3>
                </div>
                <p className="text-slate-300 text-sm mb-2 pl-10">{job.company}</p>
                <p className="text-slate-400 text-sm sm:text-base leading-relaxed max-w-xl">
                  {job.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= EDUCATION ================= */}
      <section
        ref={setSectionRef('Education')}
        data-section="Education"
        className={`w-full px-6 sm:px-8 md:px-12 py-10 sm:py-14 scroll-mt-20 ${sectionRevealClass('Education')}`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <span className="flex-1 h-px bg-white/20" />
            <span className="w-1.5 h-1.5 rotate-45 border border-white/40 flex-shrink-0" />
            <span className="flex-1 h-px bg-white/20" />
          </div>
          <SectionMark index="03" label="Education" />
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-14 sm:mb-16">Education</h2>

          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 border-t border-white/10 pt-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-md bg-white flex items-center justify-center overflow-hidden flex-shrink-0 p-2">
              <img src={buSymbol} alt="Boston University logo" className="object-contain max-w-full max-h-full" />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-xl">Boston University</h3>
              <p className="text-slate-400 text-sm mt-1 mb-4">Boston, MA · Class of 2025</p>

              <p className="text-slate-200">Bachelor of Arts in Computer Science</p>
              <p className="text-slate-400 mb-4">Minor in Business Administration and Management</p>

              <p className="text-slate-500 text-sm mb-8">
                Kilachand Honors College · Presidential Scholar · Dean's List (8 consecutive semesters)
              </p>

              <h4 className="text-white font-semibold mb-3">Teaching Assistant, 3 years</h4>
              <ul className="space-y-2.5 text-slate-400 text-sm leading-relaxed">
                <li>
                  <span className="text-slate-200 font-medium">Programming for Investments — </span>
                  Python (NumPy, pandas); models &amp; algos; historical backtests; metrics: Sharpe/drawdowns.
                </li>
                <li>
                  <span className="text-slate-200 font-medium">Introduction to Finance — </span>
                  Bonds (pricing/yield), equities &amp; securities; portfolio risk/return; time-series: returns/volatility.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================= PROJECTS ================= */}
      <section
        ref={setSectionRef('Projects')}
        data-section="Projects"
        className={`w-full px-6 sm:px-8 md:px-12 py-10 sm:py-14 scroll-mt-20 ${sectionRevealClass('Projects')}`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <span className="flex-1 h-px bg-white/20" />
            <span className="w-1.5 h-1.5 rotate-45 border border-white/40 flex-shrink-0" />
            <span className="flex-1 h-px bg-white/20" />
          </div>
          <SectionMark index="04" label="Projects" />

          <div className="flex items-baseline justify-between flex-wrap gap-4 mb-12 sm:mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl text-white">Selected work</h2>
            <div className="flex gap-4 sm:gap-5 text-[11px] uppercase tracking-[0.2em]">
              {['All', 'Software', 'Data', 'Finance'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setProjectFilter(cat)}
                  className={`pb-1 border-b transition-colors duration-200 ${
                    projectFilter === cat
                      ? 'text-[#60a5fa] border-[#60a5fa]'
                      : 'text-slate-500 border-transparent hover:text-slate-300'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-white/10">
            {filteredProjects.map((project, i) => (
              <a
                key={project.id}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-4 sm:gap-6 py-6 sm:py-7 border-b border-white/10 hover:bg-white/[0.02] transition-colors -mx-2 px-2"
              >
                <span className="font-serif italic text-slate-500 text-base sm:text-lg w-7 sm:w-8 flex-shrink-0 pt-0.5">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
                    <h3 className="text-slate-100 font-semibold group-hover:text-white transition-colors">
                      {project.title}
                    </h3>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {project.category}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed max-w-2xl">{project.description}</p>
                  <p className="text-slate-500 text-xs mt-2 tracking-wide">{project.tags.join(' · ')}</p>
                </div>
                <span className="text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0 pt-0.5">
                  →
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PERSONAL ================= */}
      <section
        ref={setSectionRef('Personal')}
        data-section="Personal"
        className={`w-full px-6 sm:px-8 md:px-12 py-10 sm:py-14 scroll-mt-20 ${sectionRevealClass('Personal')}`}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <span className="flex-1 h-px bg-white/20" />
            <span className="w-1.5 h-1.5 rotate-45 border border-white/40 flex-shrink-0" />
            <span className="flex-1 h-px bg-white/20" />
          </div>
          <SectionMark index="05" label="Personal" />
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-14 sm:mb-16">Outside of code</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 mb-20 sm:mb-24">
            {outsideOfCode.map((item) => (
              <div key={item.key}>
                <img
                  src={item.photo}
                  alt={item.title}
                  className="w-full h-52 sm:h-56 object-cover rounded-md mb-4"
                />
                <h3 className="text-white font-semibold mb-1.5">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.text}</p>
                {item.link && (
                  <a
                    href={item.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-xs uppercase tracking-wide mt-2 transition-colors"
                  >
                    <FaYoutube size={14} />
                    {item.link.label}
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Photography Gallery - masonry layout */}
          <div className="border-t border-white/10 pt-14 sm:pt-16">
            <SectionMark index="—" label="Photography" />
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-5">
              {photographyData.map((photo) => (
                <div
                  key={photo.id}
                  className="group relative mb-4 sm:mb-5 break-inside-avoid overflow-hidden rounded-md cursor-pointer"
                  onClick={() => setLightboxPhoto(photo)}
                >
                  <img
                    src={photo.url}
                    alt={photo.title || photo.description}
                    className="w-full h-auto object-cover rounded-md transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute inset-x-0 bottom-0 p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-xs tracking-wide">{photo.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= MORE ================= */}
      <section
        ref={setSectionRef('More')}
        data-section="More"
        className={`w-full px-6 sm:px-8 md:px-12 py-10 sm:py-14 scroll-mt-20 ${sectionRevealClass('More')}`}
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <span className="flex-1 h-px bg-white/20" />
            <span className="w-1.5 h-1.5 rotate-45 border border-white/40 flex-shrink-0" />
            <span className="flex-1 h-px bg-white/20" />
          </div>
          <SectionMark index="06" label="More" />
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-14 sm:mb-16">Get in touch</h2>

          <div className="grid sm:grid-cols-2 gap-10 sm:gap-12 border-t border-white/10 pt-8">
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-4">Contact</h3>
              <ul className="space-y-3 text-slate-300">
                <li>
                  <a
                    href="mailto:taydemir@bu.edu"
                    className="inline-flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <Mail className="w-4 h-4 text-slate-300" aria-hidden="true" />
                    taydemir@bu.edu
                  </a>
                </li>
                <li>
                  <a
                    href="https://github.com/tan-aydemir"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <Github className="w-4 h-4 text-slate-300" aria-hidden="true" />
                    GitHub
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.linkedin.com/in/tan-aydemir/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <Linkedin className="w-4 h-4 text-slate-300" aria-hidden="true" />
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-4">Awards</h3>
              <ul className="space-y-2.5 text-slate-300 text-sm">
                <li className="flex gap-2">
                  <span className="text-slate-500">—</span> Boston University Presidential Scholar
                </li>
                <li className="flex gap-2">
                  <span className="text-slate-500">—</span> Intel Recognition Prize
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <div
            className="relative inline-flex flex-col bg-[#16233a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 text-white text-xl z-10"
              onClick={() => setLightboxPhoto(null)}
              aria-label="Close"
            >
              ×
            </button>

            <img
              src={lightboxPhoto.url}
              alt={lightboxPhoto.title}
              className="block w-auto h-auto max-w-[92vw] max-h-[75vh] object-contain"
            />

            <div className="p-4 border-t border-white/10 w-full">
              <h3 className="text-lg font-semibold text-white">{lightboxPhoto.title}</h3>
              <p className="text-sm text-gray-400 mt-1">{lightboxPhoto.description}</p>

              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/10">
                <span className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-500">
                  <Download className="w-3.5 h-3.5" aria-hidden="true" />
                  Download
                </span>
                {['jpeg', 'png', 'webp'].map((format) => (
                  <button
                    key={format}
                    onClick={() => downloadPhoto(lightboxPhoto, format)}
                    className="text-xs uppercase tracking-[0.15em] text-slate-300 border-b border-transparent hover:text-[#60a5fa] hover:border-[#60a5fa] transition-colors pb-0.5"
                  >
                    {format === 'jpeg' ? 'JPG' : format.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalWebsite;
